import * as THREE from "three";

export class DebugManager {
  constructor(scene) {
    this.scene = scene;
    this.debugMarkers = [];
    this.infoElements = {};
    this.enabled = this.isDebugEnabled();
    this.selectedTemplate = this.getSelectedTemplate();
    this.selectedDifficulty = this.getSelectedDifficulty();
    this.initialPlayerPosition = this.getInitialPlayerPosition();
    this.difficultyWeights = { level1: 0, level2: 0, level3: 0, level4: 0 };

    // Tabbed debug overlay. Basic info (FPS, coords) stays visible at the top;
    // the rest is split into tabs the user cycles through with '[' and ']'.
    this.tabs = [
      { id: "level", label: "Level / Template" },
      { id: "profiling", label: "Profiling" },
    ];
    this.tabPanels = { level: [], profiling: [] };
    this.tabButtons = {};
    this.activeTab = this.getActiveTab();

    // Difficulty tiers (level1..level5) mapped to the names used when talking
    // about the spread. Single source of truth for both the override dropdown
    // and the forced-selection readout.
    this.difficultyOptions = [
      { value: "none", label: "None (Random)" },
      { value: "level1", label: "Easy (Level 1)" },
      { value: "level2", label: "Medium (Level 2)" },
      { value: "level3", label: "Hard (Level 3)" },
      { value: "level4", label: "Brutal (Level 4)" },
      { value: "level5", label: "Insane (Level 5)" },
    ];

    // Store latest FPS value for the getFPS helper method
    this.currentFps = 0;

    // Optional callbacks set by Game: 'o' dumps the frame profiler snapshot,
    // 'k' toggles shadow rendering (to A/B the shadow-pass cost).
    this.onDumpProfile = null;
    this.onToggleShadows = null;

    // Bind the keydown handler to this
    this.handleKeyDown = this.handleKeyDown.bind(this);

    // Create container for all debug information (even if disabled, we'll just hide it)
    this.debugContainer = document.createElement("div");
    this.debugContainer.id = "debug-container";
    this.debugContainer.style.position = "absolute";
    this.debugContainer.style.top = "10px";
    this.debugContainer.style.right = "10px";
    this.debugContainer.style.display = this.enabled ? "flex" : "none";
    this.debugContainer.style.flexDirection = "column";
    this.debugContainer.style.gap = "10px";
    this.debugContainer.style.zIndex = "1000";
    document.body.appendChild(this.debugContainer);

    // Tab bar — shows the available tabs and the active one, switchable with
    // '[' and ']' (or a click).
    this.createTabBar();

    // Basic panels: always visible regardless of the active tab.
    this.createDebugPanel("performance", "Performance");
    this.createDebugPanel("coordinates", "Player Coordinates");

    // "Level / Template" tab: the override controls sit on top, then the
    // current-template readout (which shows the live selection weights, or the
    // active override when one is pinned).
    this.createTemplateSelector("level");
    this.createDebugPanel("templateInfo", "Template Information", "level");

    // "Profiling" tab: the detailed frame profiler.
    this.createDebugPanel(
      "profiler",
      "Frame Profile (avg / p95 / max ms)",
      "profiling"
    );

    // Show only the active tab's panels.
    this.applyActiveTab();

    // Add keyboard listener for 'p' key to toggle debug mode
    this.setupKeyboardListener();

    // Log initial debug state
    console.log(
      `Debug mode initially ${
        this.enabled ? "enabled" : "disabled"
      } (toggle with 'p' key)`
    );
  }

  isDebugEnabled() {
    // Only check localStorage setting
    return localStorage.getItem("debug") === "true";
  }

  getSelectedTemplate() {
    // Only return a value if debug mode is enabled
    if (this.isDebugEnabled()) {
      return localStorage.getItem("selectedTemplate") || "none";
    }
    return "none";
  }

  getSelectedDifficulty() {
    // Only return a value if debug mode is enabled
    if (this.isDebugEnabled()) {
      return localStorage.getItem("selectedDifficulty") || "none";
    }
    return "none";
  }

  // The forced difficulty tier (e.g. "level3"), or null when debug is off or no
  // tier is pinned. When set, LevelGenerator draws every non-flat segment from
  // this tier so a whole batch can be playtested at one difficulty.
  getForceDifficulty() {
    if (this.enabled && this.selectedDifficulty !== "none") {
      return this.selectedDifficulty;
    }
    return null;
  }

  getInitialPlayerPosition() {
    // Only return a value if debug mode is enabled
    if (this.isDebugEnabled()) {
      const position = localStorage.getItem("initialPlayerPosition");
      return position ? parseFloat(position) : 0;
    }
    return 0;
  }

  getActiveTab() {
    const stored = localStorage.getItem("debugTab");
    if (stored && this.tabs.some((t) => t.id === stored)) {
      return stored;
    }
    return this.tabs[0].id;
  }

  // Build the tab bar that sits above the panels. Clicking a label or pressing
  // '[' / ']' switches which tab's panels are shown.
  createTabBar() {
    const bar = document.createElement("div");
    bar.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
    bar.style.color = "white";
    bar.style.padding = "8px 10px";
    bar.style.borderRadius = "5px";
    bar.style.fontFamily = "monospace";
    bar.style.fontSize = "14px";
    bar.style.minWidth = "300px";
    bar.style.display = "flex";
    bar.style.alignItems = "center";
    bar.style.gap = "6px";

    this.tabButtons = {};
    this.tabs.forEach((tab) => {
      const btn = document.createElement("span");
      btn.textContent = tab.label;
      btn.style.cursor = "pointer";
      btn.style.padding = "2px 8px";
      btn.style.borderRadius = "3px";
      btn.addEventListener("click", () => this.setActiveTab(tab.id));
      bar.appendChild(btn);
      this.tabButtons[tab.id] = btn;
    });

    const hint = document.createElement("span");
    hint.textContent = "[ / ] switch";
    hint.style.marginLeft = "auto";
    hint.style.color = "#888";
    hint.style.fontSize = "12px";
    bar.appendChild(hint);

    this.debugContainer.appendChild(bar);
    this.tabBar = bar;
  }

  setActiveTab(tabId) {
    if (!this.tabs.some((t) => t.id === tabId)) return;
    this.activeTab = tabId;
    localStorage.setItem("debugTab", tabId);
    this.applyActiveTab();
  }

  // Move to the previous (-1) or next (+1) tab, wrapping around.
  cycleTab(direction) {
    const index = this.tabs.findIndex((t) => t.id === this.activeTab);
    const next = (index + direction + this.tabs.length) % this.tabs.length;
    this.setActiveTab(this.tabs[next].id);
  }

  // Show only the active tab's panels and highlight its button. Basic panels
  // are untagged and always visible.
  applyActiveTab() {
    for (const tab of this.tabs) {
      const isActive = tab.id === this.activeTab;
      for (const panel of this.tabPanels[tab.id]) {
        panel.style.display = isActive ? "" : "none";
      }
      const btn = this.tabButtons[tab.id];
      if (btn) {
        btn.style.backgroundColor = isActive ? "#2196F3" : "transparent";
        btn.style.fontWeight = isActive ? "bold" : "normal";
        btn.style.color = isActive ? "white" : "#bbb";
      }
    }
  }

  createTemplateSelector(tab = null) {
    const panel = document.createElement("div");
    panel.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
    panel.style.color = "white";
    panel.style.padding = "10px";
    panel.style.borderRadius = "5px";
    panel.style.fontFamily = "monospace";
    panel.style.fontSize = "14px";
    panel.style.minWidth = "300px";

    // Add title
    const titleElement = document.createElement("div");
    titleElement.textContent = "Level Overrides";
    titleElement.style.fontWeight = "bold";
    titleElement.style.marginBottom = "5px";
    titleElement.style.borderBottom = "1px solid #666";
    panel.appendChild(titleElement);

    // Create dropdown container
    const dropdownContainer = document.createElement("div");
    dropdownContainer.style.display = "flex";
    dropdownContainer.style.alignItems = "center";
    dropdownContainer.style.gap = "10px";
    dropdownContainer.style.marginTop = "5px";

    // Create label
    const label = document.createElement("label");
    label.textContent = "Force template:";
    label.style.marginRight = "5px";
    dropdownContainer.appendChild(label);

    // Create select element
    this.templateSelect = document.createElement("select");
    this.templateSelect.style.backgroundColor = "#333";
    this.templateSelect.style.color = "white";
    this.templateSelect.style.border = "1px solid #666";
    this.templateSelect.style.padding = "5px";
    this.templateSelect.style.borderRadius = "3px";
    this.templateSelect.style.fontFamily = "monospace";

    // Add a default "none" option
    const noneOption = document.createElement("option");
    noneOption.value = "none";
    noneOption.textContent = "None (Random)";
    this.templateSelect.appendChild(noneOption);

    // Set the selected value from localStorage
    this.templateSelect.value = this.selectedTemplate;

    // Add change event listener
    this.templateSelect.addEventListener("change", (e) => {
      this.selectedTemplate = e.target.value;
      localStorage.setItem("selectedTemplate", this.selectedTemplate);
      console.log(`Template selection set to: ${this.selectedTemplate}`);
    });

    dropdownContainer.appendChild(this.templateSelect);
    panel.appendChild(dropdownContainer);

    // Create difficulty-tier dropdown container. Pinning a tier restricts every
    // non-flat segment to that difficulty so a batch can be playtested at one
    // level. A specific forced template (above) still takes precedence.
    const difficultyContainer = document.createElement("div");
    difficultyContainer.style.display = "flex";
    difficultyContainer.style.alignItems = "center";
    difficultyContainer.style.gap = "10px";
    difficultyContainer.style.marginTop = "10px";

    const difficultyLabel = document.createElement("label");
    difficultyLabel.textContent = "Force difficulty:";
    difficultyLabel.style.marginRight = "5px";
    difficultyContainer.appendChild(difficultyLabel);

    this.difficultySelect = document.createElement("select");
    this.difficultySelect.style.backgroundColor = "#333";
    this.difficultySelect.style.color = "white";
    this.difficultySelect.style.border = "1px solid #666";
    this.difficultySelect.style.padding = "5px";
    this.difficultySelect.style.borderRadius = "3px";
    this.difficultySelect.style.fontFamily = "monospace";

    for (const opt of this.difficultyOptions) {
      const option = document.createElement("option");
      option.value = opt.value;
      option.textContent = opt.label;
      this.difficultySelect.appendChild(option);
    }

    // Set the selected value from localStorage
    this.difficultySelect.value = this.selectedDifficulty;

    this.difficultySelect.addEventListener("change", (e) => {
      this.selectedDifficulty = e.target.value;
      localStorage.setItem("selectedDifficulty", this.selectedDifficulty);
      console.log(`Difficulty selection set to: ${this.selectedDifficulty}`);
    });

    difficultyContainer.appendChild(this.difficultySelect);
    panel.appendChild(difficultyContainer);

    // Create initial position input container
    const positionContainer = document.createElement("div");
    positionContainer.style.display = "flex";
    positionContainer.style.alignItems = "center";
    positionContainer.style.gap = "10px";
    positionContainer.style.marginTop = "10px";

    // Create label for position input
    const positionLabel = document.createElement("label");
    positionLabel.textContent = "Initial distance:";
    positionLabel.style.marginRight = "5px";
    positionContainer.appendChild(positionLabel);

    // Create position input
    this.positionInput = document.createElement("input");
    this.positionInput.type = "number";
    this.positionInput.value = this.initialPlayerPosition;
    this.positionInput.style.backgroundColor = "#333";
    this.positionInput.style.color = "white";
    this.positionInput.style.border = "1px solid #666";
    this.positionInput.style.padding = "5px";
    this.positionInput.style.borderRadius = "3px";
    this.positionInput.style.fontFamily = "monospace";
    this.positionInput.style.width = "70px";
    this.positionInput.min = "0";
    this.positionInput.step = "100";

    // Add change event listener
    this.positionInput.addEventListener("change", (e) => {
      const value = parseFloat(e.target.value);
      this.initialPlayerPosition = isNaN(value) ? 0 : value;
      localStorage.setItem("initialPlayerPosition", this.initialPlayerPosition);
      console.log(
        `Initial player distance set to: ${this.initialPlayerPosition}`
      );
    });

    positionContainer.appendChild(this.positionInput);
    panel.appendChild(positionContainer);

    // Add to container
    this.debugContainer.appendChild(panel);

    // Store reference to the panel
    this.templateSelectorPanel = panel;

    // Tag it onto a tab so the active-tab logic can show/hide it.
    if (tab && this.tabPanels[tab]) {
      this.tabPanels[tab].push(panel);
    }
  }

  // Method to populate the template dropdown
  populateTemplateDropdown(templates) {
    if (!this.templateSelect) return;

    // Clear existing options except the "none" option
    while (this.templateSelect.options.length > 1) {
      this.templateSelect.remove(1);
    }

    // Add options from templates
    for (const templateName of Object.keys(templates)) {
      const option = document.createElement("option");
      option.value = templateName;
      option.textContent = templateName;
      this.templateSelect.appendChild(option);
    }

    // Restore selected value
    this.templateSelect.value = this.selectedTemplate;
  }

  // Method to get the currently selected template
  getForceTemplate() {
    // Only return a value if debug mode is enabled and a template is selected
    if (this.enabled && this.selectedTemplate !== "none") {
      return this.selectedTemplate;
    }
    return null;
  }

  handleKeyDown(event) {
    // Toggle debug mode when 'p' is pressed
    if (event.key === "p") {
      this.toggleDebug();
    }

    // Cycle between debug tabs with '[' and ']' while the overlay is on.
    if (this.enabled && (event.key === "[" || event.key === "]")) {
      this.cycleTab(event.key === "]" ? 1 : -1);
    }

    // Dump a frame profiler snapshot to the console when 'o' is pressed
    // (only meaningful while the debug overlay is on).
    if (
      event.key === "o" &&
      this.enabled &&
      typeof this.onDumpProfile === "function"
    ) {
      this.onDumpProfile();
    }

    // Toggle shadow rendering when 'k' is pressed (debug overlay on).
    if (
      event.key === "k" &&
      this.enabled &&
      typeof this.onToggleShadows === "function"
    ) {
      this.onToggleShadows();
    }
  }

  setupKeyboardListener() {
    document.addEventListener("keydown", this.handleKeyDown);
  }

  createDebugPanel(id, title, tab = null) {
    const panel = document.createElement("div");
    panel.style.backgroundColor = "rgba(0, 0, 0, 0.7)";
    panel.style.color = "white";
    panel.style.padding = "10px";
    panel.style.borderRadius = "5px";
    panel.style.fontFamily = "monospace";
    panel.style.fontSize = "14px";
    panel.style.minWidth = "300px";

    // Add title
    const titleElement = document.createElement("div");
    titleElement.textContent = title;
    titleElement.style.fontWeight = "bold";
    titleElement.style.marginBottom = "5px";
    titleElement.style.borderBottom = "1px solid #666";
    panel.appendChild(titleElement);

    // Create content container
    const content = document.createElement("div");
    panel.appendChild(content);

    // Add to container
    this.debugContainer.appendChild(panel);

    // Tag it onto a tab (untagged panels are basic info, always visible).
    if (tab && this.tabPanels[tab]) {
      this.tabPanels[tab].push(panel);
    }

    // Store reference to content element
    this.infoElements[id] = content;

    return content;
  }

  toggleDebug() {
    this.enabled = !this.enabled;
    localStorage.setItem("debug", this.enabled.toString());
    this.debugContainer.style.display = this.enabled ? "flex" : "none";

    // Toggle visibility of all markers
    this.debugMarkers.forEach((marker) => {
      marker.visible = this.enabled;
    });

    // Update the selected template when toggling debug mode
    if (this.enabled) {
      this.selectedTemplate =
        localStorage.getItem("selectedTemplate") || "none";
      if (this.templateSelect) {
        this.templateSelect.value = this.selectedTemplate;
      }

      // Update the forced difficulty tier
      this.selectedDifficulty =
        localStorage.getItem("selectedDifficulty") || "none";
      if (this.difficultySelect) {
        this.difficultySelect.value = this.selectedDifficulty;
      }

      // Update initial player position input
      this.initialPlayerPosition = this.getInitialPlayerPosition();
      if (this.positionInput) {
        this.positionInput.value = this.initialPlayerPosition;
      }
    }

    console.log(
      `Debug mode ${
        this.enabled ? "enabled" : "disabled"
      } (toggle with 'p' key)`
    );
  }

  // Updates coordinate information
  updateCoordinates(player) {
    if (!this.enabled) return;

    const x = player.position.x.toFixed(2);
    const y = player.position.y.toFixed(2);
    const z = player.position.z.toFixed(2);
    const status = player.isColliding
      ? "BLOCKED"
      : player.isJumping
      ? "JUMPING"
      : player.isFalling
      ? "FALLING"
      : "RUNNING";

    this.infoElements.coordinates.innerHTML = `
      <div>Position: (${x}, ${y}, ${z})</div>
      <div>Status: ${status}</div>
      <div>Speed: ${player.speed.toFixed(3)}</div>
    `;
  }

  // Updates performance information
  updatePerformance(performanceInfo) {
    if (!this.enabled || !performanceInfo) return;

    // Store current FPS for the getFPS helper method
    this.currentFps = performanceInfo.currentFps || 0;

    // Build the HTML for the performance panel
    let performanceHtml = `
      <div>Current FPS: <strong>${performanceInfo.currentFps}</strong></div>
      <div>Average FPS: <strong>${performanceInfo.averageFps}</strong></div>
    `;

    // Add memory info if available
    if (performanceInfo.memory) {
      performanceHtml += `
        <div>Memory: <strong>${performanceInfo.memory.usedHeapSize}MB</strong> / ${performanceInfo.memory.totalHeapSize}MB</div>
      `;
    }

    // Update the performance panel
    this.infoElements.performance.innerHTML = performanceHtml;
  }

  // Updates the frame profiler panel: per-phase timings plus GPU (renderer.info)
  // and memory/GC stats. Driven by Game with FrameProfiler.getStats().
  updateProfiler(stats) {
    if (!this.enabled || !stats || !this.infoElements.profiler) return;

    // getStats() returns the same cached object between its throttled recomputes,
    // so skip the (relatively expensive) innerHTML write until the data changes.
    // This keeps the overlay from inflating the very frame times it reports.
    if (stats === this._lastProfilerStats) return;
    this._lastProfilerStats = stats;

    const ms = (v) => (v == null ? "—" : v.toFixed(2));

    // Phase rows, slowest-average first so the cost leader is at the top.
    const names = Object.keys(stats.phases).sort(
      (a, b) => stats.phases[b].avg - stats.phases[a].avg
    );

    let phaseRows = "";
    for (const name of names) {
      const p = stats.phases[name];
      phaseRows += `
        <tr>
          <td style="padding-right:10px;">${name}</td>
          <td style="text-align:right;"><strong>${ms(p.avg)}</strong></td>
          <td style="text-align:right; color:#bbb;">${ms(p.p95)}</td>
          <td style="text-align:right; color:#bbb;">${ms(p.max)}</td>
        </tr>`;
    }

    const f = stats.frame;
    const frameRow = `
        <tr style="border-top:1px solid #666;">
          <td style="padding-right:10px;"><strong>frame</strong></td>
          <td style="text-align:right;"><strong>${ms(f.avg)}</strong></td>
          <td style="text-align:right; color:#bbb;">${ms(f.p95)}</td>
          <td style="text-align:right; color:#bbb;">${ms(f.max)}</td>
        </tr>`;

    const r = stats.render;
    const rmax = stats.renderMax;
    // A steady climb in live geometry/texture counts (vs. their peak, and
    // across restarts) points at a missing dispose() somewhere.
    const renderHtml = `
      <div style="margin-top:6px; border-top:1px solid #666; padding-top:4px;">
        <div>draw calls: <strong>${r.calls}</strong> &nbsp; tris: ${r.triangles}</div>
        <div>geometries: ${r.geometries} (peak ${rmax.geometries}) &nbsp; textures: ${r.textures} (peak ${rmax.textures})</div>
        <div>programs: ${r.programs}</div>
      </div>`;

    const m = stats.memory;
    let memHtml;
    if (m.supported) {
      memHtml = `
        <div style="margin-top:6px; border-top:1px solid #666; padding-top:4px;">
          <div>heap: <strong>${
            m.usedMB == null ? "—" : Math.round(m.usedMB)
          }MB</strong> (peak ${Math.round(m.maxMB)}MB)</div>
          <div>alloc: <strong>${m.allocMBs.toFixed(
            1
          )} MB/s</strong> (proxy) &nbsp; GC: ${m.gcPerSec.toFixed(1)}/s (${
        m.gcTotal
      } total)</div>
        </div>`;
    } else {
      memHtml = `
        <div style="margin-top:6px; border-top:1px solid #666; padding-top:4px; color:#bbb;">
          heap stats need Chromium (performance.memory)
        </div>`;
    }

    this.infoElements.profiler.innerHTML = `
      <table style="border-collapse:collapse; width:100%;">
        <tbody>${phaseRows}${frameRow}</tbody>
      </table>
      ${renderHtml}
      ${memHtml}
      <div style="margin-top:6px; color:#888; font-size:12px;">'o' dump to console · 'k' toggle shadows</div>
    `;
  }

  // Generate HTML for difficulty weights visualization
  getDifficultyWeightsHtml(title = "Template Selection Weights") {
    const barHeight = 20;
    const labelWidth = 75;
    const barStyle = "background-color: #ccc; border-radius: 3px;";

    return `
      <div style="margin-top: 10px; border-top: 1px solid #666; padding-top: 5px;">
        <div style="margin-bottom: 6px;"><strong>${title}:</strong></div>
        <div style="display: flex; align-items: center; margin-top: 3px;">
          <span style="width: ${labelWidth}px;">Level 1:</span>
          <div style="flex-grow: 1; height: ${barHeight}px; ${barStyle} overflow: hidden;">
            <div style="height: 100%; background: #4CAF50; width: ${Math.round(
              this.difficultyWeights.level1 * 100
            )}%;"></div>
          </div>
          <span style="margin-left: 5px; width: 40px; text-align: right;">${Math.round(
            this.difficultyWeights.level1 * 100
          )}%</span>
        </div>
        <div style="display: flex; align-items: center; margin-top: 3px;">
          <span style="width: ${labelWidth}px;">Level 2:</span>
          <div style="flex-grow: 1; height: ${barHeight}px; ${barStyle} overflow: hidden;">
            <div style="height: 100%; background: #FFC107; width: ${Math.round(
              this.difficultyWeights.level2 * 100
            )}%;"></div>
          </div>
          <span style="margin-left: 5px; width: 40px; text-align: right;">${Math.round(
            this.difficultyWeights.level2 * 100
          )}%</span>
        </div>
        <div style="display: flex; align-items: center; margin-top: 3px;">
          <span style="width: ${labelWidth}px;">Level 3:</span>
          <div style="flex-grow: 1; height: ${barHeight}px; ${barStyle} overflow: hidden;">
            <div style="height: 100%; background: #F44336; width: ${Math.round(
              this.difficultyWeights.level3 * 100
            )}%;"></div>
          </div>
          <span style="margin-left: 5px; width: 40px; text-align: right;">${Math.round(
            this.difficultyWeights.level3 * 100
          )}%</span>
        </div>
        <div style="display: flex; align-items: center; margin-top: 3px;">
          <span style="width: ${labelWidth}px;">Level 4:</span>
          <div style="flex-grow: 1; height: ${barHeight}px; ${barStyle} overflow: hidden;">
            <div style="height: 100%; background: #2196F3; width: ${Math.round(
              this.difficultyWeights.level4 * 100
            )}%;"></div>
          </div>
          <span style="margin-left: 5px; width: 40px; text-align: right;">${Math.round(
            this.difficultyWeights.level4 * 100
          )}%</span>
        </div>
        <div style="display: flex; align-items: center; margin-top: 3px;">
          <span style="width: ${labelWidth}px;">Level 5:</span>
          <div style="flex-grow: 1; height: ${barHeight}px; ${barStyle} overflow: hidden;">
            <div style="height: 100%; background: #9C27B0; width: ${Math.round(
              this.difficultyWeights.level5 * 100
            )}%;"></div>
          </div>
          <span style="margin-left: 5px; width: 40px; text-align: right;">${Math.round(
            this.difficultyWeights.level5 * 100
          )}%</span>
        </div>
      </div>
    `;
  }

  // Updates template information
  updateTemplateInfo(generatedScenes, playerX) {
    if (!this.enabled) return;

    // Store the last values for use in updateDifficultyWeights
    this.lastGeneratedScenes = generatedScenes;
    this.lastPlayerX = playerX;

    // Find the current scene based on player position
    const currentScene = generatedScenes.find(
      (scene) => playerX >= scene.startX && playerX < scene.endX
    );

    let templateInfoHtml = "";

    if (currentScene) {
      // Continuous height offset: entry→exit floor height (cells) and the
      // vertical shift applied to chain this scene onto the previous one.
      const entryH = currentScene.entryH ?? 0;
      const exitH = currentScene.exitH ?? 0;
      const dy = currentScene.dy ?? 0;

      templateInfoHtml = `
        <div>Current Template: <strong>${currentScene.template}</strong></div>
        <div>Height H: ${entryH} → ${exitH} (dy ${dy >= 0 ? "+" : ""}${dy})</div>
        <div>Scene Range: ${currentScene.startX.toFixed(
          1
        )} to ${currentScene.endX.toFixed(1)}</div>
      `;
    } else {
      templateInfoHtml = `
        <div>Current Template: <strong>None</strong></div>
      `;
    }

    // When an override is pinned the live tier weights no longer drive
    // selection, so show what's forced instead of the (now-irrelevant) bars.
    // A specific template wins over a forced tier (matches LevelGenerator).
    const forcedTemplate = this.getForceTemplate();
    const forcedDifficulty = this.getForceDifficulty();
    let selectionHtml;
    if (forcedTemplate) {
      selectionHtml = this.getForcedSelectionHtml("Template", forcedTemplate);
    } else if (forcedDifficulty) {
      selectionHtml = this.getForcedSelectionHtml(
        "Difficulty",
        this.getDifficultyLabel(forcedDifficulty)
      );
    } else {
      selectionHtml = this.getDifficultyWeightsHtml("Template Selection Weights");
    }

    this.infoElements.templateInfo.innerHTML = templateInfoHtml + selectionHtml;
  }

  // Friendly name for a difficulty tier value (e.g. "level4" → "Brutal (Level 4)").
  getDifficultyLabel(value) {
    const opt = this.difficultyOptions.find((o) => o.value === value);
    return opt ? opt.label : value;
  }

  // Readout shown in place of the weight bars when a selection override is on.
  getForcedSelectionHtml(label, value) {
    return `
      <div style="margin-top: 10px; border-top: 1px solid #666; padding-top: 5px;">
        <div style="margin-bottom: 6px;"><strong>Selection override:</strong></div>
        <div style="display: flex; align-items: center; gap: 6px;">
          <span style="background: #2196F3; color: white; padding: 2px 8px; border-radius: 3px; font-weight: bold;">FORCED</span>
          <span>${label}: <strong>${value}</strong></span>
        </div>
        <div style="margin-top: 4px; color: #888; font-size: 12px;">tier weights paused while an override is active</div>
      </div>
    `;
  }

  // Create a marker at a specific position
  createMarker(x, y, size = 0.2, color = 0xff0000) {
    if (!this.enabled) return null;

    const geometry = new THREE.SphereGeometry(size, 16, 16);
    const material = new THREE.MeshBasicMaterial({ color });
    const marker = new THREE.Mesh(geometry, material);
    marker.position.set(x, y, 0.1); // Slightly in front to be visible
    this.scene.add(marker);
    this.debugMarkers.push(marker);
    return marker;
  }

  // Remove all markers
  clearMarkers() {
    this.debugMarkers.forEach((marker) => {
      this.scene.remove(marker);
    });
    this.debugMarkers = [];
  }

  // Remove specific markers
  removeMarkers(markers) {
    markers.forEach((marker) => {
      if (marker) {
        this.scene.remove(marker);
        const index = this.debugMarkers.indexOf(marker);
        if (index !== -1) {
          this.debugMarkers.splice(index, 1);
        }
      }
    });
  }

  // Clean up all debug elements
  cleanup() {
    this.clearMarkers();

    if (this.debugContainer && this.debugContainer.parentNode) {
      this.debugContainer.parentNode.removeChild(this.debugContainer);
    }

    // Remove keyboard event listener
    document.removeEventListener("keydown", this.handleKeyDown);

    this.infoElements = {};
  }

  // Get current framerate
  getFPS() {
    return this.currentFps;
  }

  updateDifficultyWeights(weights) {
    // Store the difficulty weights for use in the template info panel
    this.difficultyWeights = weights;

    // If we have an existing scene and player position, refresh the template info
    if (this.lastGeneratedScenes && this.lastPlayerX !== undefined) {
      this.updateTemplateInfo(this.lastGeneratedScenes, this.lastPlayerX);
    }

    // Also update the debug panel if it exists (original behavior)
    if (this.debugElement) {
      this.updateDebugPanel();
    }
  }

  updateDebugPanel() {
    // Update the debug panel with all current debug information
    if (this.debugElement) {
      // Create section for difficulty weights using shared method
      const difficultyWeightsHtml = this.difficultyWeights
        ? `
        <div class="debug-section">
          <h3>Current Difficulty</h3>
          ${this.getDifficultyWeightsHtml("Difficulty Weights")}
        </div>
      `
        : "";

      // Update the debug panel HTML
      this.debugElement.innerHTML = `
        <div class="debug-panel">
          ${difficultyWeightsHtml}
          <!-- Other debug sections can be added here -->
        </div>
      `;
    }
  }
}
